import { Cursor } from '../../models/Repository';
import {
  RewardCategory,
  RewardCategoryListField,
  RewardOffer,
  RewardOfferJson,
  RewardOfferListField,
  RewardOfferMapField,
  RewardOfferMapType,
  RewardPlaform,
  RewardRedemption,
  RewardRedemptionListField,
  RewardRedemptionTabState,
  RewardReferral,
  RewardReferralListField,
  RewardReferralType,
  RewardStoreState,
} from '../../models/RewardStore';
import { NonEmptyArray } from '../../models/Utils';

export const deleteOfferList = (
  state: RewardStoreState,
  fieldName: RewardOfferListField,
) => {
  state.offers.clear(fieldName);
  state.offers.list(fieldName, null)?.removeListener();
};

export const deleteOfferMapItem = (
  state: RewardStoreState,
  fieldName: RewardOfferMapField,
  id: string,
) => {
  state.offers.map(fieldName).delete(id);
};

export const getCategories = (
  state: RewardStoreState,
  fieldName: RewardCategoryListField,
) => state.categories.list(fieldName).get();

export const getCategory = (
  state: RewardStoreState,
  rewardCategoryId: string,
) => state.categories.get(rewardCategoryId);

export const removeCategory = (
  state: RewardStoreState,
  category: RewardCategory,
) => state.categories.delete(category.id());

export const setCategories = (
  state: RewardStoreState,
  fieldName: RewardCategoryListField,
  ...categories: RewardCategory[]
) => {
  state.categories.list(fieldName).set(...categories);
};
export const updateCategory = (
  state: RewardStoreState,
  category: RewardCategory,
) => {
  state.categories.set(category);
};

export const pushCategories = (
  state: RewardStoreState,
  fieldName: RewardCategoryListField,
  ...categories: RewardCategory[]
) => {
  state.categories.list(fieldName).push(...categories);
};

export const setCatergoryCursor = (
  state: RewardStoreState,
  fieldName: RewardCategoryListField,
  cursor: Cursor,
) => {
  state.categories.list(fieldName).setCursor(cursor);
};

export const getOffers = (
  state: RewardStoreState,
  fieldName: RewardOfferListField,
) => state.offers.list(fieldName).get();

export const getOffer = (state: RewardStoreState, rewardOfferId: string) =>
  state.offers.get(rewardOfferId);

export const setOffers = (
  state: RewardStoreState,
  fieldName: RewardOfferListField,
  ...offers: RewardOffer[]
) => {
  const field = state.offers.list(fieldName);
  if (field.isCached()) {
    field.addListener();
  }
  state.offers.list(fieldName).set(...offers);
};

export const unshiftOffers = (
  state: RewardStoreState,
  fieldName: RewardOfferListField,
  ...offers: RewardOffer[]
) => {
  const field = state.offers.list(fieldName);
  if (field.isCached()) {
    field.addListener();
  }
  state.offers.list(fieldName).unshift(...offers);
};

export const pushOffers = (
  state: RewardStoreState,
  fieldName: RewardOfferListField,
  ...offers: RewardOffer[]
) => {
  const field = state.offers.list(fieldName);
  if (field.isCached()) {
    field.addListener();
  }
  state.offers.list(fieldName).push(...offers);
};

export const setOfferCursor = (
  state: RewardStoreState,
  fieldName: RewardOfferListField,
  cursor: Cursor,
) => {
  state.offers.list(fieldName).setCursor(cursor);
};
export const getOfferCursor = (
  state: RewardStoreState,
  fieldName: RewardOfferListField,
) => {
  return state.offers.list(fieldName).getCursor();
};

export const addOfferToMap = (
  state: RewardStoreState,
  rewardRedemptionId: string,
  offer: RewardOffer | null,
) => {
  state.offers
    .map(RewardOfferMapType.ActiveUser)
    .set(rewardRedemptionId, offer);
};

export const updateOffer = (
  state: RewardStoreState,
  offerJson: RewardOfferJson,
) => {
  const offer = new RewardOffer(offerJson);
  const oldOffer = state.offers.get(offer.id());
  if (oldOffer) {
    const oldCategoryId = oldOffer.rewardCategoryId;
    const newCategoryId = offer.rewardCategoryId;
    if (oldCategoryId !== newCategoryId) {
      const store = offer.store;
      const allFieldName = `${store}_${
        RewardCategory.createAll(store).rewardCategoryId
      }`;
      const toLists: NonEmptyArray<RewardOfferListField> = [
        allFieldName,
        `${allFieldName}_admin`,
      ];
      const fromLists: NonEmptyArray<RewardOfferListField> = [
        allFieldName,
        `${allFieldName}_admin`,
      ];
      if (newCategoryId) {
        toLists.push(
          `${store}_${newCategoryId}`,
          `${store}_${newCategoryId}_admin`,
        );
      }
      if (oldCategoryId) {
        fromLists.push(
          `${store}_${oldCategoryId}`,
          `${store}_${oldCategoryId}_admin`,
        );
      }
      toLists.forEach((fieldName) => {
        const field = state.offers.list(fieldName);
        if (field.isCached()) {
          field.addListener();
        }
      });
      state.offers.lists(toLists).unshiftFrom(fromLists, offer);
    } else {
      state.offers.set(offer);
    }
  }
};

export const removeOffer = (state: RewardStoreState, offer: RewardOffer) =>
  state.offers.delete(offer.id());

export const getRedemption = (
  state: RewardStoreState,
  rewardRedemptionId: string,
) => state.redemptions.get(rewardRedemptionId);

export const getRedemptions = (
  state: RewardStoreState,
  fieldName: RewardRedemptionListField,
) => state.redemptions.list(fieldName).get();

export const setRedemptions = (
  state: RewardStoreState,
  fieldName: RewardRedemptionListField,
  store: RewardPlaform,
  ...redemptions: RewardRedemption[]
) => {
  // except "all"
  const fieldNames = [
    RewardRedemptionTabState.NotDelivered,
    RewardRedemptionTabState.Delivered,
    RewardRedemptionTabState.Completed,
    RewardRedemptionTabState.Refund,
    RewardRedemptionTabState.Pending,
    RewardRedemptionTabState.Failed,
  ].flatMap((n) => [
    `${store}_${n}`,
    `${store}_${n}_admin`,
  ]) as NonEmptyArray<RewardRedemptionListField>;
  state.redemptions.list(fieldName).setFrom(fieldNames, ...redemptions);
};
export const pushRedemptions = (
  state: RewardStoreState,
  fieldName: RewardRedemptionListField,
  store: RewardPlaform,
  ...redemptions: RewardRedemption[]
) => {
  // except "all"
  const fieldNames = [
    RewardRedemptionTabState.NotDelivered,
    RewardRedemptionTabState.Delivered,
    RewardRedemptionTabState.Completed,
    RewardRedemptionTabState.Refund,
    RewardRedemptionTabState.Pending,
    RewardRedemptionTabState.Failed,
  ].flatMap((n) => [
    `${store}_${n}`,
    `${store}_${n}_admin`,
  ]) as NonEmptyArray<RewardRedemptionListField>;
  state.redemptions.list(fieldName).pushFrom(fieldNames, ...redemptions);
};

export const setRedemptionsByOfferId = (
  state: RewardStoreState,
  offerId: string,
  store: RewardPlaform,
  ...redemptions: RewardRedemption[]
) => {
  const offer = getOffer(state, offerId);
  if (!offer) {
    return;
  }
  const fieldName = `${store}_${offerId}`;
  const field = state.redemptions.list(fieldName);
  if (field.isCached()) {
    field.addListener();
  }
  field.set(...redemptions);
};

export const pushRedemptionsByOfferId = (
  state: RewardStoreState,
  offerId: string,
  store: RewardPlaform,
  ...redemptions: RewardRedemption[]
) => {
  const offer = getOffer(state, offerId);
  if (!offer) {
    return;
  }
  const fieldName = `${store}_${offerId}`;
  const field = state.redemptions.list(fieldName);
  if (field.isCached()) {
    field.addListener();
  }
  field.push(...redemptions);
};

export const setRedemptionCursor = (
  state: RewardStoreState,
  fieldName: RewardRedemptionListField,
  cursor: Cursor,
) => {
  state.redemptions.list(fieldName).setCursor(cursor);
};

export const updateRedemption = (
  state: RewardStoreState,
  redemption: RewardRedemption,
) => {
  const oldRedemption = state.redemptions.get(redemption.id());
  if (
    oldRedemption === null ||
    (oldRedemption !== undefined && oldRedemption.isLatestVersion(redemption))
  ) {
    // avoid update with old version
    return;
  }
  const store = redemption.store;
  const tabState = redemption.getTabState();
  const allFieldName = `${store}_${RewardRedemptionTabState.All}`;
  const newFieldName = `${store}_${tabState}`;
  if (oldRedemption) {
    const oldTabState = oldRedemption.getTabState();
    const oldFieldName = `${store}_${oldTabState}`;
    state.redemptions
      .lists([
        allFieldName,
        newFieldName,
        `${allFieldName}_admin`,
        `${newFieldName}_admin`,
      ])
      .unshiftFrom(
        [
          allFieldName,
          oldFieldName,
          `${allFieldName}_admin`,
          `${oldFieldName}_admin`,
        ],
        redemption,
      );
  } else {
    state.redemptions
      .lists([
        allFieldName,
        newFieldName,
        `${allFieldName}_admin`,
        `${newFieldName}_admin`,
      ])
      .unshift(redemption);
  }
};

export const getRewardReferrals = (
  state: RewardStoreState,
  fieldName: RewardReferralListField,
) => state.referrals.list(fieldName).get();

export const getReferral = (
  state: RewardStoreState,
  rewardReferralId: string,
) => state.referrals.get(rewardReferralId);

export const removeReferral = (
  state: RewardStoreState,
  referral: RewardReferral,
) => state.referrals.delete(referral.id());

export const setRewardReferrals = (
  state: RewardStoreState,
  fieldName: RewardReferralListField,
  ...referrals: RewardReferral[]
) => {
  state.referrals.list(fieldName).set(...referrals);
};
export const updateReferral = (
  state: RewardStoreState,
  referral: RewardReferral,
) => {
  state.referrals.set(referral);
};

export const pushRewardReferrals = (
  state: RewardStoreState,
  fieldName: RewardReferralListField,
  ...referrals: RewardReferral[]
) => {
  state.referrals.list(fieldName).push(...referrals);
};

export const setRewardReferralCursor = (
  state: RewardStoreState,
  fieldName: RewardReferralListField,
  cursor: Cursor,
) => {
  state.referrals.list(fieldName).setCursor(cursor);
};

export const addRewardReferralToMap = (
  state: RewardStoreState,
  type: RewardReferralType,
  system_userId: string,
  referral: RewardReferral | null,
) => {
  state.referrals.map(type).set(system_userId, referral);
};
